import React, { useCallback, useMemo, useState, memo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Collapse
} from 'reactstrap';
import Swal from 'sweetalert2';

// Mock data - replace with your actual data structure
const mockOrderData = {
  orderId: "702-3936067-8932266",
  placedBy: "Sham GSK",
  placedDate: "July 10, 2025 9:38 AM",
  location: "Rossdale House",
  department: "RM General",
  deliverTo: {
    name: "Sham Singh",
    address: "9825 103 Street North-West, Main Office, Edmonton, Alberta",
    city: "Edmonton, Alberta T5K2M3",
    country: "Canada"
  },
  paymentMethod: "Mastercard ending in 5812",
  billingAddress: "Joel Pascua, 2016 Guardian ...",
  comments: "kindly approve",
  status: "SUBMITTED",
  deliveryStatus: "AWAITING_APPROVAL",
  items: [
    {
      id: "item_1",
      name: "Upgrade 316564511 Stove Knobs Replacements",
      description: "Only Compatible with Frigidaire Gas Range/Oven/Stove Knobs, Cooktop Burner Control Knob Replacement Part for AP4433359 (5 Packs)",
      price: 38.78,
      quantity: 1,
      total: 38.78,
      jobName: "Kitchen Maintenance",
      category: "Appliance Parts",
      sku: "316564511",
      make: "Frigidaire",
      modelNo: "AP4433359",
      image: "🔧"
    }
  ],
  totals: {
    items: 38.78,
    shipping: 0.00,
    discount: 0.00,
    tax: 0.00,
    total: 38.78
  }
};

const DELIVERY_STATUS_OPTIONS = [
  { value: 'AWAITING_APPROVAL', label: 'Awaiting Approval', color: 'warning', icon: 'fas fa-clock' },
  { value: 'APPROVED', label: 'Approved', color: 'info', icon: 'fas fa-check-circle' },
  { value: 'IN_TRANSIT', label: 'In Transit', color: 'primary', icon: 'fas fa-shipping-fast' },
  { value: 'PARTIALLY_DELIVERED', label: 'Partially Delivered', color: 'warning', icon: 'fas fa-box-open' },
  { value: 'DELIVERED', label: 'Delivered', color: 'success', icon: 'fas fa-check-double' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'danger', icon: 'fas fa-times-circle' },
  { value: 'RETURNED', label: 'Returned', color: 'secondary', icon: 'fas fa-undo' }
];

// Item Detail Modal Component
const ItemDetailModal = memo(({ isOpen, onClose, item }) => {
  if (!item) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="item-detail-modal">
      <ModalHeader toggle={onClose} className="bg-light border-bottom">
        <div className="d-flex align-items-center">
          <span className="me-2" style={{ fontSize: '1.5rem' }}>{item.image}</span>
          <div>
            <h5 className="mb-0 text-dark">Item Details</h5>
            <small className="text-muted">Order #{mockOrderData.orderId}</small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        <div className="bg-light p-4 rounded mb-4">
          <Row>
            <Col md={8}>
              <h6 className="text-primary mb-3">
                <i className="fas fa-info-circle me-2"></i>
                Product Information
              </h6>
              <div className="mb-3">
                <strong className="text-dark">Product Name:</strong>
                <p className="mb-2 text-muted">{item.name}</p>
              </div>
              <div className="mb-3">
                <strong className="text-dark">Description:</strong>
                <p className="mb-2 text-muted">{item.description}</p>
              </div>
              <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <strong className="text-dark">SKU:</strong>
                    <span className="ms-2 text-muted">{item.sku}</span>
                  </div>
                  <div className="mb-2">
                    <strong className="text-dark">Make:</strong>
                    <span className="ms-2 text-muted">{item.make}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <strong className="text-dark">Model No:</strong>
                    <span className="ms-2 text-muted">{item.modelNo}</span>
                  </div>
                  <div className="mb-2">
                    <strong className="text-dark">Category:</strong>
                    <span className="ms-2 text-muted">{item.category}</span>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={4}>
              <h6 className="text-primary mb-3">
                <i className="fas fa-shopping-cart me-2"></i>
                Order Details
              </h6>
              <div className="bg-white p-3 rounded border">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-dark">Unit Price:</span>
                  <span className="fw-bold text-primary">${item.price.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-dark">Quantity:</span>
                  <span className="fw-bold">{item.quantity}</span>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between">
                  <span className="text-dark fw-bold">Total:</span>
                  <span className="fw-bold text-success fs-5">${item.total.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-3">
                <div className="mb-2">
                  <strong className="text-dark">Job Name:</strong>
                  <p className="mb-0 text-muted">{item.jobName}</p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className="bg-primary bg-opacity-10 p-3 rounded">
          <h6 className="text-primary mb-2">
            <i className="fas fa-info-circle me-2"></i>
            Additional Information
          </h6>
          <Row>
            <Col md={6}>
              <small className="text-muted">
                <strong>Ordered by:</strong> {mockOrderData.placedBy}<br />
                <strong>Department:</strong> {mockOrderData.department}<br />
                <strong>Location:</strong> {mockOrderData.location}
              </small>
            </Col>
            <Col md={6}>
              <small className="text-muted">
                <strong>Order Date:</strong> {mockOrderData.placedDate}<br />
                <strong>Delivery to:</strong> {mockOrderData.deliverTo.name}<br />
                <strong>Status:</strong> <Badge color="warning" className="ms-1">Awaiting Approval</Badge>
              </small>
            </Col>
          </Row>
        </div>
      </ModalBody>

      <ModalFooter className="border-top bg-light">
        <Button color="secondary" onClick={onClose}>
          <i className="fas fa-times me-2"></i>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
});

ItemDetailModal.displayName = 'ItemDetailModal';

// Main RMVendor Preview Modal Component
const RMVendorPreviewModal = memo(({ isOpen, onClose, orderData = mockOrderData }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isItemDetailOpen, setIsItemDetailOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState('order-info');

  const currentStatus = useMemo(() =>
    DELIVERY_STATUS_OPTIONS.find(status => status.value === orderData.deliveryStatus) || DELIVERY_STATUS_OPTIONS[0],
    [orderData.deliveryStatus]
  );

  const handleItemClick = useCallback((item) => {
    setSelectedItem(item);
    setIsItemDetailOpen(true);
  }, []);

  const handleCloseItemDetail = useCallback(() => {
    setIsItemDetailOpen(false);
    setSelectedItem(null);
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  }, [expandedSection]);

  const handleStatusChange = useCallback(async (newStatus) => {
    const statusOption = DELIVERY_STATUS_OPTIONS.find(s => s.value === newStatus);

    const result = await Swal.fire({
      title: `Change Status to ${statusOption.label}?`,
      html: `
        <div class="text-start">
          <p class="mb-3">Are you sure you want to change the delivery status to <strong class="text-primary">${statusOption.label}</strong>?</p>
          <div class="bg-light p-3 rounded">
            <strong>Order ID:</strong> ${orderData.orderId}<br>
            <strong>Customer:</strong> ${orderData.deliverTo.name}<br>
            <strong>Total:</strong> $${orderData.totals.total.toFixed(2)}<br>
            <strong>Items:</strong> ${orderData.items.length} item(s)
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `<i class="${statusOption.icon} me-2"></i>Yes, Change Status`,
      cancelButtonText: '<i class="fas fa-times me-2"></i>Cancel',
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-secondary'
      }
    });

    if (result.isConfirmed) {
      await Swal.fire({
        title: 'Status Updated!',
        text: `Order status has been changed to ${statusOption.label}`,
        icon: 'success',
        confirmButtonColor: '#198754',
        confirmButtonText: '<i class="fas fa-check me-2"></i>OK'
      });
    }
  }, [orderData]);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        size="xl"
        className="rmvendor-preview-modal"
        style={{ maxWidth: '95%', width: '95%' }}
      >
        <ModalHeader toggle={onClose} className="bg-primary text-white">
          <div className="d-flex align-items-center w-100 justify-content-between">
            <div className="d-flex align-items-center">
              <i className="fas fa-file-invoice me-3" style={{ fontSize: '1.5rem' }}></i>
              <div>
                <h5 className="mb-0">Request Details</h5>
                <small className="opacity-75">Order #{orderData.orderId}</small>
              </div>
            </div>
            <Badge
              color={currentStatus.color}
              className="fs-6 px-3 py-2"
              style={{ borderRadius: '20px' }}
            >
              <i className={`${currentStatus.icon} me-2`}></i>
              {currentStatus.label}
            </Badge>
          </div>
        </ModalHeader>

        <ModalBody className="p-0" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Order Information Section */}
          <Card className="border-0 border-bottom">
            <CardHeader
              className="bg-light border-bottom cursor-pointer"
              onClick={() => toggleSection('order-info')}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-dark">
                  <i className="fas fa-info-circle me-2 text-primary"></i>
                  Order Information
                </h6>
                <i className={`fas ${expandedSection === 'order-info' ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              </div>
            </CardHeader>
            <Collapse isOpen={expandedSection === 'order-info'}>
              <CardBody className="p-4">
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong className="text-dark">Location:</strong>
                      <span className="ms-2 text-muted">{orderData.location}</span>
                    </div>
                    <div className="mb-3">
                      <strong className="text-dark">Department:</strong>
                      <span className="ms-2 text-muted">{orderData.department}</span>
                    </div>
                    <div className="mb-3">
                      <strong className="text-dark">Order ID:</strong>
                      <span className="ms-2 text-muted">{orderData.orderId}</span>
                    </div>
                    <div className="mb-3">
                      <strong className="text-dark">Placed by:</strong>
                      <span className="ms-2 text-muted">{orderData.placedBy} on {orderData.placedDate}</span>
                    </div>
                    <div className="mb-3">
                      <strong className="text-dark">Payment method:</strong>
                      <div className="d-flex align-items-center mt-1">
                        <div className="bg-warning text-white px-2 py-1 rounded me-2" style={{ fontSize: '0.8rem' }}>
                          <i className="fab fa-cc-mastercard"></i>
                        </div>
                        <span className="text-muted">{orderData.paymentMethod}</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light p-3 rounded">
                      <h6 className="text-primary mb-3">
                        <i className="fas fa-shipping-fast me-2"></i>
                        Delivery Information
                      </h6>
                      <div className="mb-2">
                        <strong className="text-dark">Deliver to:</strong>
                      </div>
                      <div className="text-muted">
                        <div>{orderData.deliverTo.name}</div>
                        <div>{orderData.deliverTo.address}</div>
                        <div>{orderData.deliverTo.city}</div>
                        <div>{orderData.deliverTo.country}</div>
                      </div>
                      {orderData.comments && (
                        <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded">
                          <strong className="text-dark">Requisitioner comments:</strong>
                          <div className="text-muted fst-italic mt-1">{orderData.comments}</div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Collapse>
          </Card>

          {/* Request Summary Section */}
          <Card className="border-0 border-bottom">
            <CardHeader className="bg-light border-bottom">
              <Row className="align-items-center">
                <Col md={6}>
                  <h6 className="mb-0 text-dark">
                    <i className="fas fa-list me-2 text-primary"></i>
                    {orderData.items.length} item(s) requested for approval
                  </h6>
                </Col>
                <Col md={6}>
                  <div className="text-end">
                    <div className="bg-white p-3 rounded border">
                      <div className="mb-2">
                        <span className="text-muted">Items</span>
                        <span className="float-end fw-bold">${orderData.totals.items.toFixed(2)}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted">Shipping & handling</span>
                        <span className="float-end">${orderData.totals.shipping.toFixed(2)}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted">Order discount</span>
                        <span className="float-end">${orderData.totals.discount.toFixed(2)}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted">Shipping discount</span>
                        <span className="float-end">${orderData.totals.discount.toFixed(2)}</span>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted">Total before tax</span>
                        <span className="float-end">${orderData.totals.items.toFixed(2)}</span>
                      </div>
                      <div className="mb-3">
                        <span className="text-muted">Estimated tax to be collected</span>
                        <span className="float-end">${orderData.totals.tax.toFixed(2)}</span>
                      </div>
                      <hr />
                      <div className="fs-5">
                        <span className="text-dark fw-bold">Request total</span>
                        <span className="float-end fw-bold text-danger fs-4">
                          ${orderData.totals.total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="p-4">
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <tbody>
                    {orderData.items.map((item, index) => (
                      <tr
                        key={item.id}
                        className="cursor-pointer"
                        onClick={() => handleItemClick(item)}
                        style={{ transition: 'background-color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ width: '60px' }}>
                          <div className="text-center">
                            <span style={{ fontSize: '2rem' }}>{item.image}</span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <h6 className="mb-1 text-primary">{item.name}</h6>
                            <p className="mb-2 text-muted small">{item.description}</p>
                            <div className="d-flex gap-3">
                              <small className="text-muted">
                                <strong>Job:</strong> {item.jobName}
                              </small>
                              <small className="text-muted">
                                <strong>SKU:</strong> {item.sku}
                              </small>
                              <small className="text-muted">
                                <strong>Qty:</strong> {item.quantity}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="text-danger fw-bold fs-6">
                            CA${item.total.toFixed(2)}
                          </div>
                          <div className="text-muted small">
                            Qty: {item.quantity}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          {/* Delivery Options Section */}
          <Card className="border-0">
            <CardHeader className="bg-light border-bottom">
              <h6 className="mb-0 text-dark">
                <i className="fas fa-truck me-2 text-primary"></i>
                Delivery Options
              </h6>
            </CardHeader>
            <CardBody className="p-4">
              <Row className="g-3">
                {DELIVERY_STATUS_OPTIONS.map((status) => (
                  <Col md={4} key={status.value}>
                    <Button
                      color={status.value === currentStatus.value ? status.color : 'outline-secondary'}
                      className="w-100 p-3 text-start"
                      onClick={() => handleStatusChange(status.value)}
                      disabled={status.value === currentStatus.value}
                    >
                      <div className="d-flex align-items-center">
                        <i className={`${status.icon} me-3`} style={{ fontSize: '1.2rem' }}></i>
                        <div>
                          <div className="fw-bold">{status.label}</div>
                          <small className="opacity-75">
                            {status.value === currentStatus.value ? 'Current Status' : 'Click to update'}
                          </small>
                        </div>
                      </div>
                    </Button>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        </ModalBody>

        <ModalFooter className="bg-light border-top">
          <Button color="secondary" onClick={onClose}>
            <i className="fas fa-times me-2"></i>
            Close
          </Button>
          <Button color="primary">
            <i className="fas fa-print me-2"></i>
            Print Order
          </Button>
        </ModalFooter>
      </Modal>

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={isItemDetailOpen}
        onClose={handleCloseItemDetail}
        item={selectedItem}
      />
    </>
  );
});

RMVendorPreviewModal.displayName = 'RMVendorPreviewModal';

const RMVendorOrderModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="p-4">
      <div className="text-center">
        <h2 className="mb-4 text-dark">RMVendor Preview Modal System</h2>
        <p className="text-muted mb-4">
          Click the button below to open the RMVendor preview modal with order management capabilities.
        </p>
        <Button
          color="primary"
          size="lg"
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2"
        >
          <i className="fas fa-eye me-2"></i>
          Open RMVendor Preview
        </Button>
      </div>

      <RMVendorPreviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export { RMVendorOrderModal };